import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import { ConsoleInput as MainInput, ConsoleLabel as MainLabel, ConsoleSelect as MainSelect, ConsoleTextArea, FieldError } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import useApi from '../../hooks/useApi';
import toast from 'react-hot-toast';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'create' | 'update';
  initialData?: any;
}

const FormGroup = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;`;





const PhoneRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const AddButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })`align-self: flex-start;`;

const RemoveButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })`width: 28px; padding: 0; color: ${({ theme }) => theme.color.point_n_100};`;

const PrimaryBadge = styled(Button).attrs({ $variant: "secondary", $size: "sm" })<{ $primary: boolean }>`border-color: ${({ theme, $primary }) => $primary ? theme.color.point_y_100 : theme.color.primary_300}; color: ${({ theme, $primary }) => $primary ? theme.color.point_y_100 : theme.color.text_color_300};`;

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onSuccess, mode, initialData }) => {
  const { request, loading } = useApi();
  const { request: structureRequest } = useApi();
  const [fieldStructure, setFieldStructure] = useState<any[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: '',
    phones: [{ phone: '', isPrimary: true }],
    category: '',
    memo: '',
    extensionFields: {} as Record<string, any>
  });

  useEffect(() => {
    if (isOpen) {
      const fetchStructure = async () => {
        try {
          const response = await structureRequest({
            url: '/contact/getFieldStructure',
            method: 'GET'
          });
          setFieldStructure(response || []);
        } catch (error) {
          console.error('Failed to fetch field structure', error);
        }
      };
      fetchStructure();
    }
  }, [isOpen, structureRequest]);

  useEffect(() => {
    if (mode === 'update' && initialData) {
      const extFields: Record<string, any> = {};
      if (initialData.extensionFields) {
        initialData.extensionFields.forEach((section: any) => {
          section.fields.forEach((field: any) => {
            extFields[field.key] = field.value || '';
          });
        });
      }

      setFormData({
        name: initialData.name || '',
        phones: initialData.phones && initialData.phones.length > 0
          ? initialData.phones.map((p: any) => ({ phone: p.phone || '', isPrimary: p.isPrimary || false }))
          : [{ phone: initialData.phone || '', isPrimary: true }],
        category: initialData.category || '',
        memo: initialData.memo || '',
        extensionFields: extFields
      });
    } else {
      setFormData({
        name: '',
        phones: [{ phone: '', isPrimary: true }],
        category: '',
        memo: '',
        extensionFields: {}
      });
    }
  }, [mode, initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (index: number, value: string) => {
    const newPhones = [...formData.phones];
    newPhones[index].phone = value;
    setFormData(prev => ({ ...prev, phones: newPhones }));
  };

  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, { phone: '', isPrimary: false }]
    }));
  };

  const removePhone = (index: number) => {
    if (formData.phones.length <= 1) return;
    const wasPrimary = formData.phones[index].isPrimary;
    const newPhones = formData.phones.filter((_, i) => i !== index);

    // 삭제하는 번호가 대표번호였다면 첫 번째 번호를 대표번호로 지정
    if (wasPrimary && newPhones.length > 0) {
      newPhones[0].isPrimary = true;
    }

    setFormData(prev => ({ ...prev, phones: newPhones }));
  };

  const setPrimaryPhone = (index: number) => {
    const newPhones = formData.phones.map((p, i) => ({
      ...p,
      isPrimary: i === index
    }));
    setFormData(prev => ({ ...prev, phones: newPhones }));
  };

  const handleExtensionChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      extensionFields: {
        ...prev.extensionFields,
        [key]: value
      }
    }));
  };

  const handleSubmit = async () => {
    setFieldErrors({});
    if (!formData.name) {
      setFieldErrors({ name: '이름은 필수 항목입니다.' });
      toast.error('이름은 필수 항목입니다.');
      return;
    }

    if (formData.phones.length === 0 || formData.phones.every(p => !p.phone)) {
      setFieldErrors({ phones: '최소 하나의 전화번호를 입력해야 합니다.' });
      toast.error('최소 하나의 전화번호를 입력해야 합니다.');
      return;
    }

    const primaryPhone = formData.phones.find(p => p.isPrimary);
    if (!primaryPhone || !primaryPhone.phone) {
      setFieldErrors({ phones: '대표번호는 필수이며 번호가 입력되어야 합니다.' });
      toast.error('대표번호는 필수이며 번호가 입력되어야 합니다.');
      return;
    }

    // 가변 필드 필수 여부 체크
    for (const section of fieldStructure) {
      for (const field of section.fields) {
        if (field.isRequired && !formData.extensionFields[field.key]) {
          setFieldErrors({ [field.key]: `${field.displayName}은(는) 필수 입력 항목입니다.` });
          toast.error(`${field.displayName}은(는) 필수 입력 항목입니다.`);
          return;
        }
      }
    }

    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        memo: formData.memo,
        phones: formData.phones.filter(p => p.phone),
        extensionFields: formData.extensionFields
      };

      if (mode === 'create') {
        await request({
          url: '/contact',
          method: 'POST',
          data: payload
        });
        toast.success('주소록이 성공적으로 등록되었습니다.');
      } else {
        await request({
          url: `/contact/${initialData.id}`,
          method: 'PUT',
          data: payload
        });
        toast.success('주소록이 성공적으로 수정되었습니다.');
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '처리 중 오류가 발생했습니다.';
      toast.error(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={mode === 'create' ? '신규 주소록 등록' : '주소록 정보 수정'}
      onClose={onClose}
      width={520}
      footer={<><Button $variant="secondary" onClick={onClose} disabled={loading}>취소</Button><Button onClick={handleSubmit} disabled={loading}>{loading ? '처리 중...' : mode === 'create' ? '등록하기' : '수정하기'}</Button></>}
    >
      <FormGroup>
        <MainLabel htmlFor="contact-name">고객명 *</MainLabel>
        <MainInput
          aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? "contact-name-error" : undefined} id="contact-name" name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="이름을 입력하세요"
        />
        {fieldErrors.name && <FieldError id="contact-name-error">{fieldErrors.name}</FieldError>}
      </FormGroup>

      <FormGroup>
        <MainLabel>전화번호 *</MainLabel>
        {formData.phones.map((phoneData, index) => (
          <PhoneRow key={index}>
            <MainInput
              aria-label={`${index + 1}번 전화번호`} value={phoneData.phone}
              onChange={(e) => handlePhoneChange(index, e.target.value)}
              placeholder="전화번호를 입력하세요"
            />
            <PrimaryBadge
              type="button"
              $primary={phoneData.isPrimary} aria-pressed={phoneData.isPrimary}
              aria-label={`${index + 1}번 전화번호를 대표번호로 지정`} onClick={() => setPrimaryPhone(index)}
            >
              대표번호
            </PrimaryBadge>
            <RemoveButton
              type="button"
              aria-label={`${index + 1}번 전화번호 삭제`} onClick={() => removePhone(index)}
              disabled={formData.phones.length <= 1}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </RemoveButton>
          </PhoneRow>
        ))}
        <AddButton type="button" onClick={addPhone}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          전화번호 추가
        </AddButton>
        {fieldErrors.phones && <FieldError role="alert">{fieldErrors.phones}</FieldError>}
      </FormGroup>

      <FormGroup>
        <MainLabel htmlFor="contact-category">카테고리</MainLabel>
        <MainSelect
          id="contact-category" name="category"
          value={formData.category}
          onChange={handleChange}
        >
          <option value="">카테고리 선택</option>
          <option value="CLIENT">고객</option>
          <option value="VIP">VIP</option>
          <option value="INNER">내부 관계자</option>
        </MainSelect>
      </FormGroup>

      <FormGroup>
        <MainLabel htmlFor="contact-memo">메모</MainLabel>
        <ConsoleTextArea
          id="contact-memo" name="memo"
          value={formData.memo}
          onChange={handleChange}
          placeholder="메모를 입력하세요"
          style={{ height: '80px', padding: '12px', resize: 'none' }}
        />
      </FormGroup>

      {fieldStructure.map((section: any) => (
        <React.Fragment key={section.sectionName}>
          <div style={{ margin: '24px 0 12px', fontSize: '15px', fontWeight: 700, color: 'inherit' }}>
            {section.sectionName}
          </div>
          {section.fields.map((field: any) => (
            <FormGroup key={field.key}>
              <MainLabel htmlFor={`contact-field-${field.key}`}>
                {field.displayName} {field.isRequired && <span style={{ color: '#E53E3E' }}>*</span>}
              </MainLabel>
              <MainInput
                aria-invalid={Boolean(fieldErrors[field.key])} aria-describedby={fieldErrors[field.key] ? `contact-error-${field.key}` : undefined} id={`contact-field-${field.key}`} name={field.key}
                value={formData.extensionFields[field.key] || ''}
                onChange={(e) => handleExtensionChange(field.key, e.target.value)}
                placeholder={`${field.displayName}을(를) 입력하세요`}
              />
              {fieldErrors[field.key] && <FieldError id={`contact-error-${field.key}`}>{fieldErrors[field.key]}</FieldError>}
            </FormGroup>
          ))}
        </React.Fragment>
      ))}


    </Modal>
  );
};

export default ContactModal;
